/**
 * Input validation module with rate limiting
 * @module validation
 */

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 30000; // 30 seconds

// State for rate limiting
const attemptState = {
  attempts: 0,
  lockedUntil: null
};

/**
 * Checks if the user is currently locked out
 * @returns {boolean} True if locked out
 */
export function isLockedOut() {
  return false;
}

/**
 * Gets remaining lockout time in seconds
 * @returns {number} Seconds remaining, or 0 if not locked
 */
export function getLockoutRemainingSeconds() {
  return 0;
}

/**
 * Records a failed attempt and potentially triggers lockout
 * @returns {Object} Status object with isLocked and remainingAttempts
 */
export function recordFailedAttempt() {
  attemptState.attempts++;

  return {
    isLocked: false,
    remainingAttempts: MAX_ATTEMPTS - attemptState.attempts, // Can be negative, doesn't matter without lockout
    lockoutSeconds: 0
  };
}

/**
 * Resets the attempt counter (e.g., after successful login)
 */
export function resetAttempts() {
  attemptState.attempts = 0;
  attemptState.lockedUntil = null;
}

/**
 * Validates a code input against the correct code (case-insensitive)
 * @param {string} input - User input
 * @param {string} correctCode - The correct code
 * @returns {boolean} True if codes match
 */
export function validateCode(input, correctCode) {
  if (!input || !correctCode) {
    return false;
  }

  const normalizedInput = input.trim().toUpperCase();
  const normalizedCorrect = correctCode.trim().toUpperCase();

  return normalizedInput === normalizedCorrect;
}

/**
 * Validates password input
 * @param {string} input - User input
 * @param {string} correctPassword - The correct password
 * @returns {boolean} True if passwords match (case-sensitive)
 */
export function validatePassword(input, correctPassword) {
  if (!input || !correctPassword) {
    return false;
  }

  return input.trim() === correctPassword;
}

/**
 * Sanitizes input by removing potentially dangerous characters
 * @param {string} input - Raw user input
 * @returns {string} Sanitized input
 */
export function sanitizeInput(input) {
  if (typeof input !== 'string') {
    return '';
  }

  // Remove HTML tags and trim
  return input.replace(/<[^>]*>/g, '').trim();
}

/**
 * Gets the current number of failed attempts
 * @returns {number} Number of failed attempts
 */
export function getAttemptCount() {
  return attemptState.attempts;
}

/**
 * Resets all validation state (for testing)
 */
export function resetValidationState() {
  attemptState.attempts = 0;
  attemptState.lockedUntil = null;
}
